import React from 'react';
import { Link, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { useTheme } from './ThemeProvider';

const Layout: React.FC = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const { theme, setTheme } = useTheme();

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header */}
      <motion.header 
        className="bg-background/80 backdrop-blur-sm border-b border-border sticky top-0 z-50"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        <div className="container mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Link 
                to="/" 
                className="flex items-center space-x-3 text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary-950 to-primary-600"
              >
                <motion.span 
                  className="text-3xl"
                  animate={{ rotate: [0, -10, 10, -10, 10, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 5 }}
                >
                  🥚
                </motion.span>
                <span>Egg Score</span>
              </Link>
            </motion.div>
            
            <nav className="flex items-center space-x-8">
              <motion.button
                onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
                className="p-2 rounded-lg bg-background border border-border hover:border-primary-300 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="text-xl">
                  {theme === 'light' ? '🌙' : '☀️'}
                </span>
              </motion.button>
              
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link 
                  to="/search" 
                  className="text-foreground hover:text-primary-900 dark:hover:text-primary-100 font-medium transition-all duration-300"
                >
                  Search PIs
                </Link>
              </motion.div>
              {isAuthenticated ? (
                <div className="flex items-center space-x-6">
                  <span className="text-foreground font-medium bg-primary-100 dark:bg-primary-800 px-3 py-1 rounded-full text-sm">
                    {user?.institution_domain}
                  </span>
                  <motion.button 
                    onClick={() => logout()}
                    className="btn btn-secondary"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Log Out
                  </motion.button>
                </div>
              ) : (
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Link 
                    to="/login" 
                    className="btn btn-primary"
                  >
                    Log In
                  </Link>
                </motion.div>
              )}
            </nav>
          </div>
        </div>
      </motion.header>
      
      {/* Main Content */}
      <main className="flex-grow relative">
        <div className="absolute inset-0 dots-background" />
        <motion.div 
          className="container mx-auto px-6 py-8 relative"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Outlet />
        </motion.div>
      </main>
      
      {/* Footer */}
      <footer className="bg-background/80 backdrop-blur-sm border-t border-border py-12 relative z-10">
        <div className="container mx-auto px-6">
          <motion.div 
            className="flex flex-col items-center space-y-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Link 
                to="/" 
                className="flex items-center space-x-2 text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary-950 to-primary-600"
              >
                <span className="text-2xl">🥚</span>
                <span>Egg Score</span>
              </Link>
            </motion.div>
            <p className="text-foreground text-center max-w-md">
              Rate Principal Investigators anonymously and transparently.
            </p>
            <p className="text-sm text-primary-500">
              &copy; {new Date().getFullYear()} Egg Score. All rights reserved.
            </p>
          </motion.div>
        </div>
      </footer>
    </div>
  );
};

export default Layout; 